import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import { FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import { HorizontalRule } from '@layouts/AuthLayout';

const messages = defineMessages({
  logIn: {
    id: 'GLOBAL.logIn',
    defaultMessage: 'Log in',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  password: {
    id: 'loginForm.password',
    defaultMessage: 'Password',
  },
  exampleEmail: {
    id: 'loginForm.exampleEmail',
    defaultMessage: 'example@email.com',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  emailInvalid: {
    id: 'GLOBAL.invalidEmail',
    defaultMessage: 'Invalid email',
  },
});

type LoginFormFormikProps = {
  email: string;
  password: string;
};

interface LoginFormProps {
  email?: string;
  onSubmit?: (values: LoginFormFormikProps) => Promise<void>;
}

function LoginForm({ email = '', onSubmit }: LoginFormProps) {
  const { formatMessage } = useIntl();
  const { emailSchema } = useCommonSchemas();

  // Validation schema
  const LoginFormSchema: Yup.Schema<LoginFormFormikProps> = Yup.object().shape({
    password: Yup.string().required(formatMessage(messages.required)),
    email: emailSchema.required(formatMessage(messages.required)),
  });

  async function handleSubmit(
    values: LoginFormFormikProps,
    { setSubmitting }: FormikHelpers<LoginFormFormikProps>
  ) {
    await onSubmit?.(values);
    setSubmitting(false);
  }

  return (
    <Formik
      initialValues={{
        password: '',
        email,
      }}
      onSubmit={handleSubmit}
      validationSchema={LoginFormSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormikTextField
            name="email"
            autoComplete="username"
            label={formatMessage(messages.email)}
            placeholder={formatMessage(messages.exampleEmail)}
            required
            type="email"
          />
          <FormikTextField
            name="password"
            autoComplete="current-password"
            label={formatMessage(messages.password)}
            placeholder="••••••••••"
            required
            type="password"
          />

          <HorizontalRule />

          <RoundedButton disabled={isSubmitting} type="submit">
            {formatMessage(messages.logIn)}
          </RoundedButton>
        </Form>
      )}
    </Formik>
  );
}

export default LoginForm;
